"use client"

import { MagnifyingGlassIcon } from "@heroicons/react/16/solid"
import { ChevronRightIcon } from "@heroicons/react/20/solid"
import AssetComboIcon from "@src/components/DefuseSDK/components/Asset/AssetComboIcon"
import { TriangleDownIcon, TriangleUpIcon } from "@src/icons"
import { motion, useReducedMotion } from "framer-motion"
import { enterTransition, ITEM_STAGGER, ITEMS_DELAY } from "./landingMotion"

type MarketTone = "green" | "red"

type Market = {
  symbol: string
  name: string
  icon: string
  values: readonly number[]
  tone: MarketTone
  price: number
  change: number
}

const rwaFilters = [
  "All assets",
  "24/7 Available",
  "Technology",
  "Consumer",
] as const

const cryptoFilters = ["All assets", "Layer 1", "DeFi", "Stablecoins"] as const

const perpsFilters = ["All markets", "Majors", "Memes", "DeFi"] as const

const earnFilters = ["All", "Vaults", "Staking"] as const

type EarnKind = "Staking" | "Vault"

type EarnMarket = {
  symbol: string
  name: string
  icon: string
  apy: number
  kind: EarnKind
}

const earnMarkets = [
  {
    symbol: "NEAR",
    name: "NEAR Protocol",
    icon: "/static/icons/token/near.svg",
    apy: 4.56,
    kind: "Staking",
  },
  {
    symbol: "ETH",
    name: "Ethereum",
    icon: "/static/icons/network/ethereum.svg",
    apy: 2.91,
    kind: "Staking",
  },
  {
    symbol: "Gauntlet USDC",
    name: "Yield vault",
    icon: "/static/icons/token/usdc.svg",
    apy: 4.52,
    kind: "Vault",
  },
  {
    symbol: "Taler USDC",
    name: "Yield vault",
    icon: "/static/icons/token/usdc.svg",
    apy: 6.08,
    kind: "Vault",
  },
] as const satisfies readonly EarnMarket[]

const rwaMarkets = [
  {
    symbol: "AAPL",
    name: "Apple Inc.",
    icon: "/static/icons/market/aapl.svg",
    values: [88, 82, 85, 68, 43, 34, 34, 23, 47, 37, 36, 22],
    tone: "red",
    price: 306.91,
    change: 6.58,
  },
  {
    symbol: "TSLA",
    name: "Tesla Inc.",
    icon: "/static/icons/market/tsla.png",
    values: [66, 63, 65, 55, 45, 22, 44, 50, 88, 61, 56, 48],
    tone: "red",
    price: 328.03,
    change: 11.55,
  },
  {
    symbol: "AMZN",
    name: "Amazon.com",
    icon: "/static/icons/market/amzn.svg",
    values: [28, 22, 49, 49, 59, 55, 74, 80, 88, 76, 86, 61],
    tone: "green",
    price: 275.16,
    change: 4.87,
  },
  {
    symbol: "MSFT",
    name: "Microsoft",
    icon: "/static/icons/market/msft.svg",
    values: [30, 22, 35, 36, 59, 70, 77, 79, 88, 85, 83, 60],
    tone: "green",
    price: 505.75,
    change: 12.82,
  },
  {
    symbol: "GOOGL",
    name: "Alphabet Inc.",
    icon: "/static/icons/market/googl.svg",
    values: [88, 82, 74, 61, 24, 22, 24, 25, 52, 34, 25, 30],
    tone: "red",
    price: 353.78,
    change: 5.39,
  },
  {
    symbol: "ORCL",
    name: "Oracle Corp.",
    icon: "/static/icons/market/orcl.svg",
    values: [37, 37, 45, 43, 33, 22, 36, 37, 82, 81, 88, 79],
    tone: "green",
    price: 148.08,
    change: 5.97,
  },
] as const satisfies readonly Market[]

const cryptoMarkets = [
  {
    symbol: "BTC",
    name: "Bitcoin",
    icon: "/static/icons/token/btc.svg",
    values: [88, 87, 72, 71, 54, 36, 22, 24, 27, 47, 52, 47],
    tone: "red",
    price: 64622,
    change: 1251.6,
  },
  {
    symbol: "ETH",
    name: "Ethereum",
    icon: "/static/icons/network/ethereum.svg",
    values: [88, 70, 71, 76, 72, 62, 28, 37, 47, 31, 22, 24],
    tone: "red",
    price: 1897.87,
    change: 54.67,
  },
  {
    symbol: "NEAR",
    name: "NEAR Protocol",
    icon: "/static/icons/token/near.svg",
    values: [23, 32, 53, 88, 72, 70, 77, 22, 39, 39, 23, 35],
    tone: "green",
    price: 1.65,
    change: 0.06,
  },
  {
    symbol: "SOL",
    name: "Solana",
    icon: "/static/icons/network/solana.svg",
    values: [70, 82, 88, 70, 55, 32, 24, 22, 33, 22, 28, 29],
    tone: "red",
    price: 76.48,
    change: 3.27,
  },
  {
    symbol: "DOT",
    name: "Polkadot",
    icon: "/static/icons/token/dot.svg",
    values: [24, 22, 56, 88, 63, 37, 68, 59, 56, 70, 72, 57],
    tone: "green",
    price: 0.82,
    change: 0.0184,
  },
  {
    symbol: "ZEC",
    name: "Zcash",
    icon: "/static/icons/token/zec.svg",
    values: [86, 88, 84, 81, 77, 72, 73, 46, 38, 37, 28, 22],
    tone: "red",
    price: 506.76,
    change: 16.75,
  },
] as const satisfies readonly Market[]

const perpsMarkets = [
  {
    symbol: "HYPE",
    name: "Hyperliquid",
    icon: "/static/icons/network/hyperliquid.svg",
    values: [37, 36, 22, 24, 38, 42, 46, 64, 71, 65, 86, 88],
    tone: "green",
    price: 54.67,
    change: 2.5,
  },
  {
    symbol: "SOL",
    name: "Solana",
    icon: "/static/icons/network/solana.svg",
    values: [82, 88, 79, 63, 70, 47, 44, 22, 35, 34, 31, 44],
    tone: "red",
    price: 76.48,
    change: 1.88,
  },
  {
    symbol: "DOGE",
    name: "Dogecoin",
    icon: "/static/icons/network/dogecoin.svg",
    values: [81, 88, 83, 60, 65, 35, 33, 25, 22, 28, 52, 56],
    tone: "red",
    price: 0.0698,
    change: 0.0026,
  },
  {
    symbol: "WIF",
    name: "dogwifhat",
    icon: "/static/icons/token/dogwifhat.jpg",
    values: [47, 53, 35, 50, 24, 27, 22, 38, 56, 68, 69, 88],
    tone: "green",
    price: 0.1439,
    change: 0.0069,
  },
  {
    symbol: "ONDO",
    name: "Ondo",
    icon: "/static/icons/token/ondo.svg",
    values: [60, 88, 68, 58, 28, 30, 29, 25, 53, 58, 42, 22],
    tone: "red",
    price: 0.3493,
    change: 0.006,
  },
  {
    symbol: "SUI",
    name: "Sui",
    icon: "/static/icons/network/sui.svg",
    values: [76, 74, 80, 65, 45, 29, 25, 22, 88, 72, 73, 72],
    tone: "red",
    price: 0.6943,
    change: 0.0207,
  },
] as const satisfies readonly Market[]

export type ExploreHref = "/crypto" | "/rwa" | "/perps" | "/earn"
export type MarketsHref = Exclude<ExploreHref, "/earn">

const EXPLORE_PAGES = {
  "/crypto": {
    suffix: "crypto",
    filters: cryptoFilters,
    searchPlaceholder: "Search assets",
  },
  "/rwa": {
    suffix: "real-world assets",
    filters: rwaFilters,
    searchPlaceholder: "Search assets",
  },
  "/perps": {
    suffix: "perpetual futures",
    filters: perpsFilters,
    searchPlaceholder: "Search markets",
  },
  "/earn": {
    suffix: "earn",
    filters: earnFilters,
    searchPlaceholder: "Search assets",
  },
} as const

const MARKET_PAGES = {
  "/crypto": cryptoMarkets,
  "/rwa": rwaMarkets,
  "/perps": perpsMarkets,
} as const

export function LandingExplorePage({ href }: { href: ExploreHref }) {
  return (
    <section className="mx-auto max-w-3xl overflow-hidden p-8">
      <ExploreHeader href={href} />
      <ExploreContent href={href} />
    </section>
  )
}

export function LandingRwaPreview() {
  return <LandingExplorePage href="/rwa" />
}

export function LandingCryptoPreview() {
  return <LandingExplorePage href="/crypto" />
}

export function LandingPerpsPreview() {
  return <LandingExplorePage href="/perps" />
}

export function LandingEarnPreview() {
  return <LandingExplorePage href="/earn" />
}

export default LandingCryptoPreview

export function ExploreHeader({ href }: { href: ExploreHref }) {
  const page = EXPLORE_PAGES[href]

  return (
    <header>
      <h2 className="mb-6 flex h-9 items-center font-medium text-gray-900 text-xl/7 tracking-tight">
        Explore {page.suffix}
      </h2>

      <div className="flex items-center justify-between gap-3 pb-5">
        <div className="-outline-offset-1 flex h-9 w-48 shrink-0 select-none items-center gap-2 rounded-lg bg-gray-50 px-3 outline outline-gray-150">
          <MagnifyingGlassIcon className="size-4 shrink-0 text-gray-500" />
          <span className="font-medium text-gray-500 text-sm/5">
            {page.searchPlaceholder}
          </span>
        </div>

        <div className="flex h-9 min-w-0 items-center justify-end gap-1.5 overflow-hidden">
          {page.filters.map((filter) => (
            <span
              key={filter}
              className="-outline-offset-1 flex h-9 shrink-0 select-none items-center whitespace-nowrap rounded-full bg-gray-50 px-4 font-medium text-gray-500 text-sm/5 tracking-tight outline outline-gray-150"
            >
              {filter}
            </span>
          ))}
        </div>
      </div>
    </header>
  )
}

export function ExploreContent({ href }: { href: ExploreHref }) {
  return (
    <div>
      {href === "/earn" ? (
        <EarnTable />
      ) : (
        <MarketsGrid href={href} markets={MARKET_PAGES[href]} />
      )}
    </div>
  )
}

function MarketsGrid({
  href,
  markets,
}: {
  href: MarketsHref
  markets: readonly Market[]
}) {
  const reduceMotion = useReducedMotion()
  const animateEnter = !reduceMotion

  return (
    <div key={href} className="grid grid-cols-2 gap-3">
      {markets.map((market, index) => (
        <motion.div
          key={market.symbol}
          {...enterTransition(ITEMS_DELAY + index * ITEM_STAGGER, animateEnter)}
        >
          <MarketCard {...market} />
        </motion.div>
      ))}
    </div>
  )
}

function EarnTable() {
  const reduceMotion = useReducedMotion()
  const animateEnter = !reduceMotion
  const innerDelay = ITEMS_DELAY + ITEM_STAGGER

  return (
    <div key="/earn" className="flow-root">
      <motion.div
        className="w-full min-w-0 max-w-full overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 align-middle"
        {...enterTransition(ITEMS_DELAY, animateEnter)}
      >
        <div className="w-full overflow-x-auto">
          <div className="inline-block min-w-full px-1 pb-1 align-middle">
            <table className="relative min-w-full border-separate border-spacing-0 divide-y divide-gray-200 overflow-x-auto">
              <thead>
                <motion.tr
                  {...enterTransition(innerDelay, animateEnter, { y: false })}
                >
                  <th
                    scope="col"
                    className="py-2.5 pr-3 pl-4 text-left font-medium text-gray-500 text-sm/5 sm:pl-5"
                  >
                    <span className="sr-only">Icon</span>
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-left font-medium text-gray-500 text-sm/5"
                  >
                    Asset
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-right font-medium text-gray-500 text-sm/5"
                  >
                    APY
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-right font-medium text-gray-500 text-sm/5"
                  >
                    Type
                  </th>
                  <th
                    scope="col"
                    className="py-2.5 pr-4 pl-3 text-right font-medium text-gray-500 text-sm/5 sm:pr-5"
                  >
                    <span className="sr-only">Actions</span>
                  </th>
                </motion.tr>
              </thead>
              <tbody className="bg-white [&>tr+tr>td]:border-gray-200 [&>tr+tr>td]:border-t">
                {earnMarkets.map((market, index) => (
                  <EarnRow
                    key={market.symbol}
                    {...market}
                    delay={innerDelay + (index + 1) * ITEM_STAGGER}
                    animateEnter={animateEnter}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

function MarketCard({
  symbol,
  name,
  icon,
  values,
  tone,
  price,
  change,
}: Market) {
  const isPositive = tone === "green"
  const ChangeIcon = isPositive ? TriangleUpIcon : TriangleDownIcon
  const previousPrice = isPositive ? price - change : price + change
  const changePercent = previousPrice === 0 ? 0 : (change / previousPrice) * 100

  return (
    <article className="flex flex-col gap-4 overflow-hidden rounded-xl border border-gray-200 bg-white">
      <header className="flex items-center px-4 pt-4">
        <div className="flex min-w-0 items-center gap-3">
          <AssetComboIcon icon={icon} sizeClassName="size-10" />
          <div className="min-w-0">
            <h3 className="truncate font-medium text-base/5 text-gray-900 tracking-tight">
              {symbol}
            </h3>
            <p className="truncate font-medium text-gray-500 text-sm/4">
              {name}
            </p>
          </div>
        </div>
      </header>

      <div className="flex flex-col px-4">
        <div className="font-medium text-gray-900 text-xl/6 tracking-tight">
          ${formatPrice(price)}
        </div>
        <div
          className={
            isPositive
              ? "flex items-center gap-0.5 text-green-700"
              : "flex items-center gap-0.5 text-red-600"
          }
        >
          <div className="flex size-4 items-center justify-center">
            <ChangeIcon className="size-4" />
          </div>
          <div className="font-medium text-sm/5 tracking-tight">
            {changePercent.toFixed(2)}%
          </div>
        </div>
      </div>

      <div className="h-25 w-full shrink-0">
        <AreaChart symbol={symbol} values={values} tone={tone} />
      </div>
    </article>
  )
}

function EarnRow({
  symbol,
  name,
  icon,
  apy,
  kind,
  delay,
  animateEnter,
}: EarnMarket & { delay: number; animateEnter: boolean }) {
  return (
    <motion.tr
      className="pointer-events-none select-none [&>td:first-child]:border-l first:[&>td:first-child]:rounded-tl-xl last:[&>td:first-child]:rounded-bl-xl [&>td:last-child]:border-r first:[&>td:last-child]:rounded-tr-xl last:[&>td:last-child]:rounded-br-xl [&>td]:border-gray-200 first:[&>td]:border-t last:[&>td]:border-b"
      {...enterTransition(delay, animateEnter, { y: false })}
    >
      <td className="size-px whitespace-nowrap">
        <div className="flex size-full items-center justify-center py-4 pr-3 pl-4 text-left sm:pl-5">
          <AssetComboIcon
            icon={icon}
            sizeClassName="size-10"
            className="shrink-0"
          />
        </div>
      </td>
      <td className="h-px whitespace-nowrap">
        <div className="flex size-full flex-col items-start justify-center px-3 py-4 text-left">
          <span className="font-medium text-base/5 text-gray-900 tracking-tight">
            {symbol}
          </span>
          <span className="font-medium text-gray-500 text-sm/5">{name}</span>
        </div>
      </td>
      <td className="h-px whitespace-nowrap">
        <div className="flex size-full items-center justify-end px-3 py-4 text-right font-medium text-base/5 text-gray-900">
          {apy.toFixed(2)}%
        </div>
      </td>
      <td className="h-px whitespace-nowrap">
        <div className="flex size-full items-center justify-end px-3 py-4 text-right font-medium text-base/5 text-gray-900">
          {kind}
        </div>
      </td>
      <td className="h-px whitespace-nowrap">
        <div className="flex size-full flex-col items-end justify-center py-4 pr-4 pl-3 text-right sm:pr-5">
          <ChevronRightIcon className="size-5 shrink-0 text-gray-500" />
        </div>
      </td>
    </motion.tr>
  )
}

function formatPrice(price: number) {
  if (price >= 1000) {
    return price.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  if (price >= 1) {
    return price.toFixed(2)
  }

  const digits = price >= 0.01 ? 4 : 6
  return price.toFixed(digits).replace(/\.?0+$/, "")
}

function AreaChart({
  symbol,
  values,
  tone,
}: {
  symbol: string
  values: readonly number[]
  tone: MarketTone
}) {
  const width = 300
  const height = 160
  const topPadding = 4
  const bottomPadding = 16
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = Math.max(max - min, 1)
  const points = values.map((value, index) => ({
    x: (index / (values.length - 1)) * width,
    y:
      topPadding +
      ((max - value) / range) * (height - topPadding - bottomPadding),
  }))
  const linePath = points.reduce((path, point, index) => {
    if (index === 0) return `M ${point.x} ${point.y}`
    const previous = points[index - 1]
    if (!previous) return path
    const midpoint = (previous.x + point.x) / 2
    return `${path} C ${midpoint} ${previous.y}, ${midpoint} ${point.y}, ${point.x} ${point.y}`
  }, "")
  const areaPath = `${linePath} L ${width} ${height} L 0 ${height} Z`
  const gradientId = `market-area-${symbol.toLowerCase()}-${tone}`
  const color =
    tone === "red" ? "var(--color-red-500)" : "var(--color-green-600)"

  return (
    <div className="size-full">
      <svg
        aria-hidden="true"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="size-full"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.24" />
            <stop offset="100%" stopColor={color} stopOpacity="0.08" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${gradientId})`} />
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth="4"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  )
}
